import { Text } from '@timelyapp/react';
import { RadioButton } from 'components/radio-new/RadioButton';
import RadioGroup from 'components/radio-new/RadioGroup';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { type AppImportOptions, SyncOptions, updateDraftOptions } from 'redux/modules/apps.types';
import type { ThunkDispatch } from 'types/redux';
import styles from './Options.module.scss';

interface Props {
  draftOptions: AppImportOptions | null;
  hasOptions: boolean;
  entityName: string;
  parentSource?: string;
  childSource: string;
  showWorkspaceOption?: boolean;
}

const SyncOptionsSection: React.FC<Props> = ({
  draftOptions,
  hasOptions,
  entityName,
  parentSource,
  childSource,
  showWorkspaceOption = true,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const convertDraftOptionsToDisplayOptions = (draftOptions: AppImportOptions | null) => {
    if (draftOptions?.sync_all === true) {
      return SyncOptions.SYNC_EVERYTHING;
    }
    if (draftOptions?.parent_source === childSource) {
      return SyncOptions.SYNC_FROM_ENTITY;
    }
    if (draftOptions?.parent_source === parentSource) {
      return SyncOptions.SYNC_EVERYTHING_FROM_WORKSPACE;
    }
  };

  const convertDisplayOptionsToDraftOptions = useCallback(
    (selectedOption: SyncOptions) => {
      switch (selectedOption) {
        case SyncOptions.SYNC_EVERYTHING:
          return {
            sync_all: true,
            parent_source: undefined,
          };
        case SyncOptions.SYNC_FROM_ENTITY:
          return {
            sync_all: false,
            parent_source: childSource,
          };
        case SyncOptions.SYNC_EVERYTHING_FROM_WORKSPACE:
          return {
            sync_all: false,
            parent_source: parentSource,
          };
      }
    },
    [parentSource, childSource]
  );

  const selectedOption = convertDraftOptionsToDisplayOptions(draftOptions);

  const handleSyncOptionChange = useCallback(
    (selectedOption: SyncOptions) => {
      const draftOptions = convertDisplayOptionsToDraftOptions(selectedOption);
      dispatch(updateDraftOptions(draftOptions));
    },
    [convertDisplayOptionsToDraftOptions, dispatch]
  );

  return (
    <>
      <Text.Label className={styles.label}>Sync</Text.Label>
      <RadioGroup<SyncOptions>
        className={styles.radioGroup}
        selectedValue={selectedOption}
        onChangeSelected={handleSyncOptionChange}
      >
        <RadioButton disabled={hasOptions} value={SyncOptions.SYNC_EVERYTHING}>
          Sync everything
        </RadioButton>
        <RadioButton disabled={hasOptions} value={SyncOptions.SYNC_FROM_ENTITY}>
          Sync from selected {entityName}
        </RadioButton>
        {showWorkspaceOption && (
          <RadioButton disabled={hasOptions} value={SyncOptions.SYNC_EVERYTHING_FROM_WORKSPACE}>
            Sync everything from selected workspace
          </RadioButton>
        )}
      </RadioGroup>
    </>
  );
};

export default SyncOptionsSection;
